import { TrackManagerCredentialsIndexCredential } from "@/types/track_manager_credentials_index";
import { Badge, Flex, Text } from "@radix-ui/themes";
import {
  AccessorFnColumnDef,
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";
import { dateRangeFilter } from "@/shared/tables/utility";
import { badgeColors } from "@/shared/constants/constants";
import BasicTable from "../BasicTable";

type Credential = TrackManagerCredentialsIndexCredential;

interface Props {
  credentials: Credential[];
  setSelectedRowIndex: React.Dispatch<React.SetStateAction<number | null>>;
  setFilteredRows: React.Dispatch<
    React.SetStateAction<Row<Credential>[] | null | undefined>
  >;
}

export default function CredentialsTable({
  credentials,
  setSelectedRowIndex,
  setFilteredRows,
}: Props) {
  const [data, setData] = useState(() => [...credentials]);

  useEffect(() => {
    setData([...credentials]);
  }, [credentials]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const columnHelper = createColumnHelper<Credential>();

  const columns = useMemo<ColumnDef<Credential, any>[]>(
    () => [
      columnHelper.accessor("email", {
        header: "Applicant",
        accessorFn: (row) => `${row.email} ${row.form_title}`,
        cell: (props) => (
          <Flex direction="column">
            <Text>{props.row.original.email}</Text>
            <Text color="gray" size="1">
              {props.row.original.form_title}
            </Text>
          </Flex>
        ),
        meta: { filterVariant: "text" },
      } as AccessorFnColumnDef<Credential, any>),
      columnHelper.accessor("track_name", {
        header: "Track",
        meta: { filterVariant: "text" },
      }),
      columnHelper.accessor("category", {
        header: "Category",
        cell: (props) => (
          <Badge variant="soft" color="gray">
            <Text as="p" style={{ textTransform: "capitalize" }}>
              {props.getValue()}
            </Text>
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          selectOptions: [
            { value: "all", title: "All" },
            { value: "photography", title: "Photography" },
            { value: "drone", title: "Drone" },
            { value: "support", title: "Support" },
          ],
        },
      }),
      columnHelper.accessor("submitted_at_raw", {
        header: "Submitted",
        cell: (props) => props.row.original.submitted_at,
        filterFn: dateRangeFilter,
        meta: { filterVariant: "dateRange" },
      }),
      columnHelper.accessor("expires_at_raw", {
        header: "Expires",
        cell: (props) => (
          <Flex align="center" gap="2">
            <Text>{props.row.original.expires_at}</Text>
            {props.row.original.expired && (
              <Badge variant="soft" color="crimson">
                Expired
              </Badge>
            )}
          </Flex>
        ),
        filterFn: dateRangeFilter,
        meta: { filterVariant: "dateRange" },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (props) => (
          <Badge variant="soft" color={badgeColors[props.getValue()]}>
            <Text as="p" style={{ textTransform: "capitalize" }}>
              {props.getValue()}
            </Text>
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          selectOptions: [
            { value: "all", title: "All" },
            { value: "pending", title: "Pending" },
            { value: "approved", title: "Approved" },
            { value: "rejected", title: "Rejected" },
          ],
        },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const filteredRows = table.getRowModel().rows;

  useEffect(() => {
    setFilteredRows(filteredRows);
  }, [filteredRows, columnFilters]);

  return (
    <BasicTable
      table={table}
      handleRowOnClick={(_: Row<Credential>, idx?: number) =>
        idx !== undefined && setSelectedRowIndex(idx)
      }
    />
  );
}
